import { useCallback, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { EMPTY_BRIEF, submissionErrorMessage, validateBrief } from "@/data/brief";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MINIMUM_SUBMIT_LOCK_MS = 800;

const waitForSubmitLock = async (startedAt) => {
  const remaining = MINIMUM_SUBMIT_LOCK_MS - (performance.now() - startedAt);
  if (remaining > 0) {
    await new Promise((resolve) => window.setTimeout(resolve, remaining));
  }
};

export const useBriefSubmission = () => {
  const [form, setForm] = useState(EMPTY_BRIEF);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false);
  const attempt = useRef(null);

  const setField = useCallback((key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  }, []);

  const resetDone = useCallback(() => setDone(false), []);

  const submit = useCallback(async (event) => {
    event.preventDefault();
    if (inFlight.current) return;

    setError("");
    const validationError = validateBrief(form);
    if (validationError) {
      setError(validationError);
      return;
    }

    const fingerprint = JSON.stringify(form);
    if (!attempt.current || attempt.current.fingerprint !== fingerprint) {
      attempt.current = { fingerprint, id: crypto.randomUUID() };
    }

    const lockStartedAt = performance.now();
    inFlight.current = true;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/leads`, {
        ...form,
        submission_id: attempt.current.id,
        page_url: window.location.href,
      }, { timeout: 15000 });
      if (data.stored !== true || !data.id) throw new Error("Storage was not confirmed");
      setDone(true);
      setForm(EMPTY_BRIEF);
      attempt.current = null;
    } catch (requestError) {
      const status = requestError.response?.status;
      if (status === 409) attempt.current = null;
      const message = submissionErrorMessage(status);
      setError(message);
      toast.error(message);
    } finally {
      await waitForSubmitLock(lockStartedAt);
      inFlight.current = false;
      setBusy(false);
    }
  }, [form]);

  return {
    form,
    busy,
    done,
    error,
    inFlight,
    dirty: Object.values(form).some(Boolean),
    setField,
    resetDone,
    submit,
  };
};